/**
 * Custom GA4 event wiring beyond what Shopify's native Google & YouTube
 * channel already sends automatically (page_view, view_item, add_to_cart,
 * begin_checkout, purchase are NOT handled here — verified already firing
 * natively, adding them here would duplicate them). This file covers events
 * with no native equivalent:
 *
 *   Link clicks (one delegated listener, one event per click):
 *     whatsapp_click       any wa.me / api.whatsapp.com link
 *     phone_click          tel: links
 *     email_click          mailto: links
 *     directions_click     Google Maps links
 *     social_click         Facebook / Instagram / TikTok links
 *     wholesale_cta_click  links to the wholesale registration or landing
 *                          page, and the landing page's "#registro" buttons
 *   Every click event carries link_location (the button's data-wa-button /
 *   data-wl-cta label, else the section it sits in), page_type and
 *   page_location.
 *
 *   view_cart              cart drawer opened with items
 *   wholesale_registration + generate_lead
 *                          Shopify Forms (wholesale sign-up) submitted
 *   order_request_created  fired by assets/whatsapp-checkout.js, not here
 *
 * wholesale_registration detection: the wholesale signup form is a
 * third-party app block (Shopify Forms, forms.shopifyapps.com) with no theme
 * code to hook into, and its submission is hCaptcha-gated, which blocked full
 * automated verification of this specific detector (see launch checklist).
 * It works by watching for a successful mutating response from that app's
 * domain, since that's the only reliable signal available from outside the
 * app's own sandbox.
 */

(function () {
  function track(eventName, params) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, params);
  }

  function pageType() {
    const path = window.location.pathname;
    if (path === '/' || path === '') return 'home';
    if (path.startsWith('/products/')) return 'product';
    if (path.startsWith('/collections/')) return 'collection';
    if (path.startsWith('/pages/')) return 'page:' + path.split('/')[2];
    if (path.startsWith('/blogs/')) return 'blog';
    if (path.startsWith('/cart')) return 'cart';
    if (path.startsWith('/search')) return 'search';
    if (path.startsWith('/account')) return 'account';
    if (path.startsWith('/policies/')) return 'policy';
    return 'other';
  }

  function linkLocation(link) {
    if (link.dataset.waButton) return link.dataset.waButton;
    if (link.dataset.wlCta) return link.dataset.wlCta;
    if (link.hasAttribute('data-continue-whatsapp')) return 'order_request_continue';
    const section = link.closest('[id^="shopify-section-"]');
    if (!section) return 'unknown';
    return section.id
      .replace('shopify-section-', '')
      .replace(/^(sections|template)--\d+__/, '');
  }

  const WHOLESALE_PATHS = ['/pages/registro-mayorista', '/pages/panaderia-mayorista'];

  function classifyLink(link) {
    const href = link.getAttribute('href') || '';
    if (href.startsWith('tel:')) return { event: 'phone_click' };
    if (href.startsWith('mailto:')) return { event: 'email_click' };
    if (/(^|\/\/)(wa\.me|api\.whatsapp\.com)\//.test(href)) return { event: 'whatsapp_click' };
    if (/(maps\.google\.|google\.[a-z.]+\/maps|goo\.gl\/maps|maps\.app\.goo\.gl)/.test(href)) {
      return { event: 'directions_click' };
    }
    const social = href.match(/\/\/(?:www\.|m\.)?(facebook|instagram|tiktok)\.com/);
    if (social) return { event: 'social_click', params: { social_network: social[1] } };

    if (href.startsWith('#') && link.dataset.wlCta) {
      return { event: 'wholesale_cta_click', params: { link_target: href } };
    }

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch (e) {
      return null;
    }
    const sameSite = url.origin === window.location.origin;
    if (sameSite && WHOLESALE_PATHS.some((path) => url.pathname.startsWith(path))) {
      return { event: 'wholesale_cta_click', params: { link_target: url.pathname } };
    }
    if (link.dataset.wlCta) {
      return { event: 'wholesale_cta_click', params: { link_target: url.hash || url.pathname } };
    }
    return null;
  }

  // One delegated listener for every tracked link type, so a click can never
  // fire two of these events. Catches links added later too (the
  // "Continuar por WhatsApp" button the order-request flow renders).
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href]');
    if (!link) return;

    const match = classifyLink(link);
    if (!match) return;

    track(match.event, {
      link_url: link.href.split('?')[0],
      link_location: linkLocation(link),
      page_type: pageType(),
      page_location: window.location.href,
      ...(match.params || {}),
    });
  });

  // view_cart — Shopify's native channel does not send this automatically
  // (verified). Fires once per cart-drawer open, only when the cart is
  // non-empty (an empty-cart peek carries no purchase intent signal).
  let lastCartViewOpenState = false;

  function checkCartDrawerState() {
    const dialog = document.querySelector('cart-drawer-component dialog, #cart-drawer-component dialog, dialog[aria-label="Carrito"], dialog[aria-label="Cart"]');
    const isOpen = !!(dialog && dialog.open);

    if (isOpen && !lastCartViewOpenState) {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((res) => res.json())
        .then((cart) => {
          if (!cart.items || cart.items.length === 0) return;
          track('view_cart', {
            currency: cart.currency,
            value: cart.total_price / 100,
            item_count: cart.item_count,
            page_location: window.location.href,
          });
        })
        .catch(() => {});
    }

    lastCartViewOpenState = isOpen;
  }

  const cartObserver = new MutationObserver(checkCartDrawerState);
  cartObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['open'],
    subtree: true,
  });

  // wholesale_registration — see file-level comment. Watches for a
  // successful mutating response from the Forms app's own domain.
  const FORMS_APP_HOST = 'forms.shopifyapps.com';
  const MUTATING_METHODS = ['POST', 'PUT', 'PATCH'];

  // A single sign-up can make more than one successful request to the app;
  // count it once per 10 seconds so GA4 and Google Ads see one lead.
  let lastRegistrationAt = 0;
  function trackRegistration() {
    const now = Date.now();
    if (now - lastRegistrationAt < 10000) return;
    lastRegistrationAt = now;
    const params = { page_location: window.location.href, page_type: pageType() };
    track('wholesale_registration', params);
    track('generate_lead', { ...params, lead_source: 'wholesale_form' });
    // Lets the landing page show "step 2: log in" (sections/wl-products-form.liquid).
    document.dispatchEvent(new CustomEvent('wholesale:registered'));
  }

  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input?.url;
    const method = (init?.method || (typeof input === 'object' && input.method) || 'GET').toUpperCase();

    return originalFetch.apply(this, arguments).then((response) => {
      if (
        url &&
        url.includes(FORMS_APP_HOST) &&
        MUTATING_METHODS.includes(method) &&
        response.ok
      ) {
        trackRegistration();
      }
      return response;
    });
  };

  const OriginalXHR = window.XMLHttpRequest;
  function PatchedXHR() {
    const xhr = new OriginalXHR();
    let trackedUrl = '';
    let trackedMethod = '';

    const originalOpen = xhr.open;
    xhr.open = function (method, url) {
      trackedMethod = (method || '').toUpperCase();
      trackedUrl = url || '';
      return originalOpen.apply(xhr, arguments);
    };

    xhr.addEventListener('load', () => {
      if (
        trackedUrl.includes(FORMS_APP_HOST) &&
        MUTATING_METHODS.includes(trackedMethod) &&
        xhr.status >= 200 &&
        xhr.status < 300
      ) {
        trackRegistration();
      }
    });

    return xhr;
  }
  window.XMLHttpRequest = PatchedXHR;
})();
